import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '@/shared/api-client';
import { Forbidden } from '@/modules/auth/Forbidden';
import { useAuth } from '@/modules/auth/AuthContext';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { getPublicWaiverTemplate } from '@/modules/public-waivers/publicWaivers';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { Button } from '@/shared/ui/components/button';
import {
  AssignWaiverTemplateDialog,
  publicWaiverSigningUrl,
  VenueWaiverQrCode,
  type VenueWaiverAssignmentTarget,
} from '@/modules/venues/VenueWaiverControls';
import { useWaiverTemplates } from '@/modules/venues/venues';

function venueWaiverQueryKey(venueId: string) {
  return ['venue-waiver-page', venueId] as const;
}

export function VenueWaiverPage() {
  const { t } = useTranslation('common');
  const { venueId } = useVenueContext();
  const { venues } = useAuth();
  const auth = useAuthorization();
  const canManage = auth.can('venue.waiver_assignment.manage', { kind: 'venue', venueId });
  const venueName = venues.find((venue) => venue.id === venueId)?.name ?? venueId;
  const [assigning, setAssigning] = useState(false);

  const activeTemplateQuery = useQuery({
    queryKey: venueWaiverQueryKey(venueId),
    queryFn: () => getPublicWaiverTemplate(venueId),
    enabled: canManage,
  });
  const templatesQuery = useWaiverTemplates();

  if (!canManage) {
    return <Forbidden />;
  }

  const noActiveWaiver =
    activeTemplateQuery.error instanceof ApiError &&
    activeTemplateQuery.error.code === 'venue_has_no_active_waiver_template';

  const templates = templatesQuery.data?.items ?? [];
  const activeTemplate = activeTemplateQuery.data ?? null;
  const assignedTemplate = activeTemplate
    ? (templates.find((template) => template.id === activeTemplate.templateId) ?? null)
    : null;
  const assignmentTarget: VenueWaiverAssignmentTarget = {
    id: venueId,
    name: venueName,
    activeWaiverTemplate: activeTemplate
      ? {
          templateId: activeTemplate.templateId,
          version: activeTemplate.version,
        }
      : null,
  };
  const signingUrl = publicWaiverSigningUrl(venueId);
  const activeTemplateName = assignedTemplate?.name ?? t('venues.waivers.activeTemplateFallback');

  return (
    <section className="grid gap-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">{t('venues.waivers.pageTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('venues.waivers.scopedDescription', { name: venueName })}
        </p>
      </div>

      {activeTemplateQuery.isLoading && (
        <p className="text-muted-foreground">{t('venues.waivers.activeLoading')}</p>
      )}
      {activeTemplateQuery.isError && !noActiveWaiver && (
        <p className="text-destructive">{t('venues.waivers.activeLoadFailed')}</p>
      )}
      {noActiveWaiver && (
        <div className="flex items-center justify-between gap-3 rounded-xl border bg-card p-6">
          <p className="text-muted-foreground">{t('venues.waivers.unassigned')}</p>
          <Button
            variant="outline"
            onClick={() => setAssigning(true)}
            disabled={templates.length === 0 || templatesQuery.isLoading}
          >
            {t('venues.waivers.actions.assign')}
          </Button>
        </div>
      )}
      {templatesQuery.isLoading && (
        <p className="text-muted-foreground">{t('venues.waivers.loading')}</p>
      )}
      {templatesQuery.isError && (
        <p className="text-destructive">{t('venues.waivers.errors.loadFailed')}</p>
      )}

      {activeTemplate && (
        <div className="grid max-w-xl gap-4 rounded-xl border bg-card p-6">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">{t('venues.waivers.activeTemplate')}</p>
              <p className="text-lg font-medium">
                {activeTemplateName} · v{activeTemplate.version}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setAssigning(true)}
              disabled={templates.length === 0 || templatesQuery.isLoading}
            >
              <RefreshCw className="mr-1 size-4" />
              {t('venues.waivers.actions.change')}
            </Button>
          </div>

          <ul className="grid gap-1 text-sm text-muted-foreground">
            {activeTemplate.acknowledgements.length === 0 && (
              <li>{t('venues.waivers.noAcknowledgements')}</li>
            )}
            {activeTemplate.acknowledgements.map((ack, index) => (
              <li key={`${ack.text}-${index}`}>
                {ack.required
                  ? t('venues.waivers.requiredAcknowledgement')
                  : t('venues.waivers.optionalAcknowledgement')}{' '}
                {ack.text}
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-4 border-t pt-4">
            <VenueWaiverQrCode
              value={signingUrl}
              ariaLabel={t('venues.detail.waiverSigningLink.qrCodeAriaLabel')}
            />
            <div>
              <p className="text-sm font-medium">{t('venues.detail.waiverSigningLink.title')}</p>
              <a
                href={signingUrl}
                target="_blank"
                rel="noreferrer"
                className="break-all text-xs text-primary underline underline-offset-4"
              >
                {signingUrl}
              </a>
            </div>
          </div>
        </div>
      )}

      <AssignWaiverTemplateDialog
        venue={assigning ? assignmentTarget : null}
        templates={templates}
        onOpenChange={(open) => !open && setAssigning(false)}
        onSaved={() => {
          void activeTemplateQuery.refetch();
          toast.success(t('venues.waivers.toast.assigned'));
        }}
      />
    </section>
  );
}
